import { useState } from 'react';
import { useOffice } from '@components/OfficeLoader';

interface Slide {
  slide_number: number;
  title: string;
  text_boxes: string[];
}

interface Slidedeck {
  file_name: string;
  slides: Slide[];
}
export function usePowerPointSlides() {
  const { officeReady } = useOffice();
  const [slideDeck, setSlideDeck] = useState<Slidedeck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [presentationInfoError, setPresentationInfoError] = useState<string | null>(null);

  const getPresentationTitle = (): string => {
    try {
      const fileUrl = Office?.context?.document?.url;

      if (fileUrl) {
        const parts = fileUrl.split('/');
        const fileName = parts.length > 0 ? parts.pop()?.trim() || null : null;

        // Remove .pptx or any extension
        const rawTitle = fileName?.replace(/\.[^/.]+$/, '');

        // Remove empty spaces and replace with underscores
        const title = rawTitle?.replace(/\s+/g, '_');

        return title ?? 'Untitled_Presentation';
      }
    } catch (error) {
      console.error('Error retrieving presentation title:', error);
    }

    return 'Untitled_Presentation';
  };

  const readSlides = async (): Promise<Slidedeck | null> => {
    if (!officeReady) return null;

    setError(null);

    try {
      let localSlideDeck: Slidedeck | null = null;

      await PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        slides.load('items');
        await context.sync();

        console.log(`Found ${slides.items.length} slide(s)`);

        const slideResults: Slide[] = [];
        let slidesWithText = 0;

        for (let i = 0; i < slides.items.length; i++) {
          const slide = slides.items[i];
          const textBoxes: string[] = [];

          console.log(`--- Processing Slide ${i + 1} ---`);

          const shapes = slide.shapes;
          shapes.load('items/type');
          await context.sync();

          const potentialTextShapes = shapes.items.filter(shape =>
            shape.type === PowerPoint.ShapeType.placeholder ||
            shape.type === PowerPoint.ShapeType.textBox
          );

          console.log(`Slide ${i + 1} has ${potentialTextShapes.length} potential text shape(s)`);

          for (let s = 0; s < potentialTextShapes.length; s++) {
            const shape = potentialTextShapes[s];

            try {
              shape.load({
                textFrame: {
                  textRange: {
                    text: true,
                  },
                },
              });
              await context.sync();

              const text = shape.textFrame?.textRange?.text?.trim();
              if (text) {
                console.log(`Found text in shape ${s + 1}: "${text}"`);
                textBoxes.push(text);

                // Skip remaining shapes if we already found text
                if (textBoxes.length > 0) {
                  // console.log(`Skipping remaining ${potentialTextShapes.length - s - 1} shapes on slide ${i + 1}`);
                  break;
                }
              }
            } catch (e) {
              console.warn(`Error processing shape ${s + 1}:`, e);
            }
          }

          slideResults.push({
            slide_number: i + 1,
            title: `Slide ${i + 1}`,
            text_boxes: textBoxes,
          });

          if (textBoxes.length > 0) {
            slidesWithText++;
          }

          // Stop processing if we have at least 5 slides with text
          if (slidesWithText >= 5) {
            // console.log(`Found ${slidesWithText} slides with text, skipping remaining ${slides.items.length - i - 1} slides`);
            break;
          }
        }

        localSlideDeck = {
          file_name: getPresentationTitle(),
          slides: slideResults,
        };

        setSlideDeck(localSlideDeck);
      });

      return localSlideDeck;
    } catch (err) {
      console.error('Error in readSlides:', err);
      setError((err as Error).message);
      return null;
    }
  };

  const getPresentationInfo = async (): Promise<{ title: string } | null> => {
    if (!officeReady) return null;

    setPresentationInfoError(null);

    try {
      const presentationTitle = getPresentationTitle();

      const result: { title: string } = {
        title: presentationTitle || 'Untitled',
      };

      return result;
    } catch (err) {
      console.error('Error in getPresentationInfo:', err);
      setPresentationInfoError((err as Error).message);
      return null;
    }
  };

  return { slideDeck, error, readSlides, getPresentationInfo, presentationInfoError };
}
